import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  loadedSelectionHeaderState,
  mergeLoadedSelection,
  pageClientRows,
  searchClientRows,
} from '../../lib/clientSelectionList';
import { dashboardT } from '../../lib/messages';

const selectionDialogPageSizes = [5, 10, 25, 50, 100] as const;
const defaultPageSize = 10;

type DashboardSelectionToolbarAfterSearch<TItem> =
  React.ReactNode | ((searchedItems: TItem[]) => React.ReactNode);

interface DashboardSelectionDialogProps<TItem> {
  open: boolean;
  title: string;
  description?: string;
  items: TItem[];
  selected: TItem[];
  loading: boolean;
  error: boolean;
  getId: (item: TItem) => string;
  getName: (item: TItem) => string;
  initialPageSize?: number | undefined;
  matches?: ((item: TItem) => boolean) | undefined;
  clientFilterKey?: string | undefined;
  columns?: DashboardDataTableColumnDefinition<TItem>[] | undefined;
  toolbarAfterSearch?: DashboardSelectionToolbarAfterSearch<TItem> | undefined;
  searchLabel?: string;
  nameHeader?: string;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  minWidthClassName?: string;
  onConfirm: (items: TItem[]) => void;
  onOpenChange: (open: boolean) => void;
}

export function DashboardSelectionDialog<TItem>(props: DashboardSelectionDialogProps<TItem>) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.open ? (
        <DashboardSelectionDialogContent
          key={props.selected.map(props.getId).join(':')}
          {...props}
        />
      ) : null}
    </Dialog>
  );
}

function DashboardSelectionDialogContent<TItem>(props: DashboardSelectionDialogProps<TItem>) {
  // An editable copy: this content remounts on every open, so each open starts from `selected`.
  // react-doctor-disable-next-line react-doctor/no-derived-useState
  const [draft, setDraft] = React.useState(props.selected);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(props.initialPageSize ?? defaultPageSize);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [appliedFilterKey, setAppliedFilterKey] = React.useState(props.clientFilterKey);
  if (props.clientFilterKey !== appliedFilterKey) {
    setAppliedFilterKey(props.clientFilterKey);
    setPage(0);
  }
  const selectedIds = new Set(draft.map(props.getId));
  const search = useDebouncedValue(
    searchTerm,
    (nextSearchTerm) => {
      setSearchTerm(nextSearchTerm);
      setPage(0);
    },
    650,
  );
  const paged = pageClientRows({
    items: props.items,
    searchTerm,
    searchText: props.getName,
    page,
    pageSize,
    matches: props.matches,
  });
  const searchedItems = searchClientRows({
    items: props.items,
    searchTerm,
    searchText: props.getName,
  });
  const toolbarAfterSearch =
    typeof props.toolbarAfterSearch === 'function'
      ? props.toolbarAfterSearch(searchedItems)
      : props.toolbarAfterSearch;
  const headerState = loadedSelectionHeaderState(selectedIds, paged.filteredItems.map(props.getId));
  const columns = buildDashboardDataTableColumns<TItem>([
    {
      id: 'select',
      kind: DashboardDataTableColumnKind.Selection,
      checked: (item) => selectedIds.has(props.getId(item)),
      label: props.getName,
      headerChecked: headerState.checked,
      headerDisabled: headerState.disabled,
      headerLabel: dashboardT('common.selectAll'),
      onHeaderChange: (checked) => {
        setDraft((current) =>
          mergeLoadedSelection(
            current,
            paged.filteredItems,
            checked,
            props.getId,
            props.getId,
            (item) => item,
          ),
        );
      },
      onChange: (item, checked) => {
        setDraft((current) =>
          checked
            ? [...current.filter((candidate) => props.getId(candidate) !== props.getId(item)), item]
            : current.filter((candidate) => props.getId(candidate) !== props.getId(item)),
        );
      },
      className: 'w-12',
    },
    {
      id: 'name',
      header: props.nameHeader ?? 'Name',
      kind: DashboardDataTableColumnKind.Text,
      value: props.getName,
      className: 'min-w-[260px]',
    },
    ...(props.columns ?? []),
  ]);
  return (
    <DashboardTableDialogContent>
      <DashboardTableDialogHeader title={props.title} description={props.description} />
      <DashboardTableDialogBody>
        <DashboardTableDialogDataTable
          tableLabel={props.title}
          columns={columns}
          items={paged.items}
          getRowKey={props.getId}
          loading={props.loading}
          loadingMessage={props.loadingMessage ?? 'Loading…'}
          errorMessage={props.error ? (props.errorMessage ?? 'Unable to load items.') : undefined}
          emptyMessage={props.emptyMessage ?? 'No items found.'}
          minWidthClassName={props.minWidthClassName ?? 'min-w-[520px]'}
          toolbar={
            <>
              <DashboardTableSearchInput
                aria-label={props.searchLabel ?? 'Search'}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
              {toolbarAfterSearch}
            </>
          }
          pagination={{
            value: {
              page: paged.page,
              pageSize,
              totalItems: paged.totalItems,
            },
            pageSizes: [...selectionDialogPageSizes],
            disabled: props.loading,
            onChange: (next) => {
              setPage(next.page);
              setPageSize(next.pageSize);
            },
          }}
        />
      </DashboardTableDialogBody>
      <DashboardTableDialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {props.cancelLabel ?? 'Cancel'}
        </Button>
        <Button
          type="button"
          onClick={() => {
            props.onConfirm(draft);
            props.onOpenChange(false);
          }}
        >
          {props.confirmLabel ?? 'Select'}
        </Button>
      </DashboardTableDialogFooter>
    </DashboardTableDialogContent>
  );
}
